import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Spinner } from "@/components/Common/Spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:opacity-70",
        "primary-alt":
          "bg-none text-primary hover:text-primary-foreground hover:bg-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/70",
        "destructive-alt":
          "bg-none text-destructive hover:text-accent-foreground hover:bg-destructive ",
        success: "bg-success text-success-foreground hover:bg-success/70",
        "success-alt":
          "bg-none text-success hover:text-success-foreground hover:bg-success ",
        warning: "bg-warning text-warning-foreground hover:opacity-70",
        "warning-alt":
          "bg-none text-warning hover:text-warning-foreground hover:bg-warning ",
        outline:
          "border border-input bg-white hover:bg-accent hover:text-accent-foreground text-primary",
        secondary: "bg-secondary text-secondary-foreground hover:opacity-70",
        ghost: "text-accent hover:opacity-70",
        "ghost-primary": "text-primary hover:opacity-70",
        "ghost-alt":
          "hover:bg-accent hover:text-accent-foreground text-primary-foreground",

        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-accent text-accent-foreground hover:opacity-70",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-lg px-8",
        lgIcon: "h-12 w-12",
        icon: "h-10 w-10",
        smIcon: "h-8 w-8",
        tinyIcon: "h-6 w-6",
        pill: "h-8 rounded-full px-2 text-xs",
        tinyPill: "h-4 rounded-full px-2 text-3xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      asChild = false,
      loading = false,
      fullWidth = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          fullWidth && "w-full"
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? (
              <div className="mr-2 flex">
                <Spinner size="sm" />{" "}
              </div>
            ) : null}
            {children}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
